import { useState, useEffect, useCallback } from 'react'

const DB_NAME = 'av-training-modules'
const DB_VERSION = 1
const STORE_NAME = 'modules'

interface CachedModule {
  id: string
  metadata: any
  content: any
  cachedAt: number
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
  })
}

async function getCachedModuleFromDB(moduleId: string): Promise<CachedModule | null> {
  try {
    const db = await openDB()
    const transaction = db.transaction(STORE_NAME, 'readonly')
    const store = transaction.objectStore(STORE_NAME)
    const request = store.get(moduleId)

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  } catch {
    return null
  }
}

async function cacheModuleToDB(moduleId: string, metadata: any, content: any): Promise<void> {
  try {
    const db = await openDB()
    const transaction = db.transaction(STORE_NAME, 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    const cachedModule: CachedModule = {
      id: moduleId,
      metadata,
      content,
      cachedAt: Date.now(),
    }
    store.put(cachedModule)
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
    })
  } catch {
    // Silently fail if IndexedDB is unavailable
  }
}

export function useCachedModule() {
  const [cacheStatus, setCacheStatus] = useState<Record<string, 'idle' | 'caching' | 'cached'>>({})

  const getCachedModule = useCallback(async (moduleId: string) => {
    return getCachedModuleFromDB(moduleId)
  }, [])

  const cacheModule = useCallback(async (moduleId: string, metadata: any, content: any) => {
    setCacheStatus((prev) => ({ ...prev, [moduleId]: 'caching' }))
    await cacheModuleToDB(moduleId, metadata, content)
    setCacheStatus((prev) => ({ ...prev, [moduleId]: 'cached' }))
  }, [])

  const isModuleCached = useCallback(
    async (moduleId: string) => {
      const cached = await getCachedModuleFromDB(moduleId)
      return cached !== null
    },
    []
  )

  useEffect(() => {
    // Listen for service worker messages about offline readiness
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'MODULE_CACHED') {
        setCacheStatus((prev) => ({ ...prev, [event.data.moduleId]: 'cached' }))
      }
    }

    navigator.serviceWorker?.addEventListener('message', handleMessage)
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage)
    }
  }, [])

  return { getCachedModule, cacheModule, isModuleCached, cacheStatus }
}
